import DidsAPI from 'dashboard/api/asterisk/dids';
import { createHandleRealtimeEvent } from './_helpers/asteriskRealtime';
import { createPendingOp } from './_helpers/asteriskAsyncOp';

const RESOURCE = 'did';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isCloning: false,
  },
};

export const getters = {
  getRecords: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getById: $state => id =>
    $state.records.find(r => r.id === id || r.id === Number(id)),
  getByDidNumber: $state => didNumber =>
    $state.records.find(r => r.did_number === didNumber),
  getByType: $state => type =>
    $state.records.filter(r => r.destination_type === type),
  getActiveDids: $state =>
    $state.records.filter(r => r.active === 1 || r.active === true),
};

export const actions = {
  // Recebe broadcast ActionCable disparado pelo ApiCallJob (DidsService) e
  // resolve a Promise pendente do create/update/delete via resolvePendingOp.
  handleRealtimeEvent: createHandleRealtimeEvent('fetch', RESOURCE),

  async fetch({ commit }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await DidsAPI.get();
      const dids = response.data.dids || [];
      commit('SET_RECORDS', dids);
      return dids;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async show(_, id) {
    const response = await DidsAPI.show(id);
    return response.data.did;
  },

  // Backend retorna 202 (job enfileirado). Promise resolve quando broadcast
  // `asterisk.did.changed` confirma — permite toast.promise no form.
  async create({ commit }, data) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      await DidsAPI.create({ did: data });
      await createPendingOp(RESOURCE, 'created', null);
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, data }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      await DidsAPI.update(id, { did: data });
      await createPendingOp(RESOURCE, 'updated', id);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await DidsAPI.delete(id);
      await createPendingOp(RESOURCE, 'deleted', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async clone({ commit }, { id, newDidNumber }) {
    commit('SET_UI_FLAG', { isCloning: true });
    try {
      await DidsAPI.clone(id, newDidNumber);
      await createPendingOp(RESOURCE, 'created', newDidNumber);
    } finally {
      commit('SET_UI_FLAG', { isCloning: false });
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_RECORDS($state, records) {
    $state.records = records;
  },

  ADD_RECORD($state, record) {
    $state.records.push(record);
  },

  UPDATE_RECORD($state, { id, data }) {
    const index = $state.records.findIndex(
      r => r.id === id || r.id === Number(id)
    );
    if (index !== -1) {
      $state.records[index] = { ...$state.records[index], ...data };
    }
  },

  DELETE_RECORD($state, id) {
    $state.records = $state.records.filter(
      r => r.id !== id && r.id !== Number(id)
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
